// ─── Workspace settings (timezone, name, etc.) ────────────────────────────────
// Lightweight client-side mirror of /api/workspaces/me. Fetched once per page
// load and reactive to PATCH /api/workspaces/me. Used by the datetime helpers
// to format dates in the workspace's IANA timezone instead of the browser's.

import React from 'react'
import { apiClient } from '@/lib/api'

export interface WorkspaceSettings {
  id: string | null
  name: string | null
  timezone: string
}

const STORAGE_KEY = 'workspace.settings.v1'
const FALLBACK_TZ = 'America/Chicago'

const DEFAULTS: WorkspaceSettings = {
  id: null,
  name: null,
  timezone: FALLBACK_TZ,
}

function loadFromStorage(): WorkspaceSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULTS
    const parsed = JSON.parse(raw) as Partial<WorkspaceSettings>
    return {
      id: parsed.id ?? null,
      name: parsed.name ?? null,
      timezone: parsed.timezone || FALLBACK_TZ,
    }
  } catch {
    return DEFAULTS
  }
}

let _workspacePromise: Promise<WorkspaceSettings> | null = null

function fetchWorkspaceOnce(): Promise<WorkspaceSettings> {
  if (!_workspacePromise) {
    _workspacePromise = apiClient
      .get<{ success: boolean; data: { id: string; name: string; timezone: string } }>(
        '/api/workspaces/me',
      )
      .then((res) => {
        const data = res.data.data
        const ws: WorkspaceSettings = {
          id: data.id,
          name: data.name,
          timezone: data.timezone || FALLBACK_TZ,
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(ws))
        return ws
      })
      .catch(() => {
        _workspacePromise = null
        return loadFromStorage()
      })
  }
  return _workspacePromise
}

export async function saveWorkspaceTimezone(timezone: string): Promise<WorkspaceSettings> {
  const res = await apiClient.patch<{
    success: boolean
    data: { id: string; name: string; timezone: string }
  }>('/api/workspaces/me', { timezone })
  return applyWorkspaceSettings(res.data.data)
}

/**
 * Publish a fresh workspace payload (after any PATCH /api/workspaces/me) so
 * every useWorkspace() consumer — timezone formatting, the app name — updates.
 */
export function applyWorkspaceSettings(data: { id: string; name: string; timezone?: string | null }): WorkspaceSettings {
  const ws: WorkspaceSettings = {
    id: data.id,
    name: data.name,
    timezone: data.timezone || FALLBACK_TZ,
  }
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(ws)) } catch { /* private mode */ }
  _workspacePromise = null
  window.dispatchEvent(new CustomEvent('workspace-settings-updated', { detail: ws }))
  return ws
}

/**
 * Remember the business name from the sign-in payload (/api/auth/me sends
 * user.workspace), so the shell can name the business even when
 * /api/workspaces/me is slow or fails. A different tenant id replaces the
 * whole cache rather than mixing two tenants.
 */
export function rememberWorkspaceName(ws: { id?: string | null; name?: string | null } | null | undefined): void {
  const name = typeof ws?.name === 'string' ? ws.name.trim() : ''
  if (!name) return
  const cached = loadFromStorage()
  const sameTenant = !cached.id || !ws?.id || cached.id === ws.id
  if (sameTenant && cached.name === name) return
  const next: WorkspaceSettings = {
    id: ws?.id ?? cached.id,
    name,
    timezone: sameTenant ? cached.timezone : FALLBACK_TZ,
  }
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)) } catch { /* private mode */ }
  window.dispatchEvent(new CustomEvent('workspace-settings-updated', { detail: next }))
}

/** Reactive hook — returns workspace settings, refreshed on save events. */
export function useWorkspace(): WorkspaceSettings {
  const [ws, setWs] = React.useState<WorkspaceSettings>(() => loadFromStorage())

  React.useEffect(() => {
    let active = true
    fetchWorkspaceOnce().then((fetched) => {
      if (active) setWs(fetched)
    })
    return () => { active = false }
  }, [])

  React.useEffect(() => {
    const handler = (e: Event) => {
      const custom = e as CustomEvent<WorkspaceSettings>
      if (custom.detail) setWs(custom.detail)
    }
    window.addEventListener('workspace-settings-updated', handler)
    return () => window.removeEventListener('workspace-settings-updated', handler)
  }, [])

  return ws
}

/**
 * Synchronous read of the workspace timezone — for callers outside React
 * (formatters, sorters, event-loop tasks). Returns the cached value; falls
 * back to the IANA default. Pair with useWorkspace() inside React components.
 */
export function getWorkspaceTimezone(): string {
  return loadFromStorage().timezone || FALLBACK_TZ
}
